/* eslint-disable no-param-reassign */
/* eslint-disable no-underscore-dangle */
import React, { useState, useContext, useEffect, useCallback, useMemo } from 'react';
import _ from 'lodash';
import { useTranslator } from '@jutro/locale';
import { ViewModelForm, ViewModelServiceContext } from '@xengage/gw-portals-viewmodel-react';
import { MLNameDetailsComponent, MLAddressDetailsComponent } from 'ml-components-common-react'
import { MLNotificationBlockUtil } from 'ml-portals-util-react';
import { MLTagManagerService } from 'ml-portals-services-react'

import MLChosenRecipientsTable from './MLChosenRecipientsTable'
import metadata from './MLDocumentRecipients.metadata.json5';
import styles from './MLDocumentRecipients.module.scss';
import messages from './MLDocumentRecipients.messages';

function MLDocumentRecipients(props) {
    const {
        isMultipleSigner,
        multipleSigners,
        potentialRecipients,
        updatePotentialRecipients,
        chosenRecipients,
        updateChosenRecipients,
        tagServiceFormName,
        isEsignForm,
        canEmailDocument
    } = props;
    const translator = useTranslator();
    const viewModelService = useContext(ViewModelServiceContext);
    const alertService = MLNotificationBlockUtil();
    
    const E_SIG = useMemo(() => "esignature_ml",[]);
    const [currentRecipient, updateCurrentRecipient] = useState()
    const [tempID, updateTempID] = useState(1)
    const [isinputDisabled, updateIsInputDisabled] = useState(false);
    const [emailRecipientsCount, setEmailRecipientsCount] = useState(0)
    const [faxRecipientsCount, setFaxRecipientsCount] = useState(0)

    const checkDeliveryMethods = useCallback((_currentRecipient) => {
        if(!isEsignForm){
            const currentESignMethod = _currentRecipient.distributionMethod.aspects.availableValues.find((method) => {
                return method.code === E_SIG;
            });
            const currentESignMethodIdx = _currentRecipient.distributionMethod.aspects.availableValues.indexOf(currentESignMethod);
            if (currentESignMethodIdx > -1) {
                _currentRecipient.distributionMethod.aspects.availableValues.splice(currentESignMethodIdx, 1);
            }
        }
    }, [E_SIG, isEsignForm])

    const _initCurrentRecipient = () => {
        const _currentRecipient = viewModelService.create({}, 'pc', 
            'metlife.edge.capabilities.gateway.ondemanddocs.dto.OnDemandDocumentRecipientDTOML'
        );
        
        checkDeliveryMethods(_currentRecipient)
        
        return _currentRecipient;
    }

    const _initOtherRecipient = () => {
        if (!isMultipleSigner) {
            const existingOther = potentialRecipients.find((recipient) => {
                return !recipient.publicID && recipient.tempID;
            });
            const existingOtherIdx = potentialRecipients.indexOf(existingOther);
            const _potentialRecipients = [...potentialRecipients]
            
            if (existingOtherIdx > -1) {
                _potentialRecipients.splice(existingOtherIdx, 1);
            }

            _potentialRecipients.push({
                tempID,
                subtype: 'Person',
                skipPrivacyDetails_ML: true,
                primaryAddress: {}
            });
            updateTempID(tempID+1);
            updatePotentialRecipients(_potentialRecipients);
        }
    };

    const _initMultipleSigner = (signer, secondary, _currentRecipient) => {
        const _potentialRecipients = [];
        
        _potentialRecipients.push(signer);
        
        // eslint-disable-next-line prefer-destructuring, no-param-reassign
        _currentRecipient.contact = _potentialRecipients[0];
        
        if (secondary) {
            _currentRecipient.distributionMethod = chosenRecipients[0].distributionMethod;
        }
        
        updatePotentialRecipients(_potentialRecipients);
    };


    useEffect(() => {
        const _currentRecipient = _initCurrentRecipient();

        _initOtherRecipient();

        if (isMultipleSigner) {
            _initMultipleSigner(multipleSigners.primarySigner, false, _currentRecipient);
        }

        updateCurrentRecipient(_currentRecipient);

        if (!canEmailDocument) {
            alertService.addWarningAlert(messages.emailSubTypeNotAllowed, 'emailSubTypeMessage');
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const getContactDisplayName = (contact) => {
        if (contact.displayName) {
            return contact.displayName;
        }

        if (contact.firstName && contact.lastName) {
            return `${contact.firstName} ${contact.lastName}`;
        }
        
        return translator(messages.other);
    };

    const addRecipientsAvailableValues = () => {
        return potentialRecipients.map((recipient) => {
            return {
                code: recipient.publicID || recipient.tempID,
                name: getContactDisplayName(recipient)
            }
        });
    }
    
    const handleChange = useCallback((value, path) => {
        const newCurrentRecipient = viewModelService.clone(currentRecipient);
        _.set(newCurrentRecipient, path, value);
        checkDeliveryMethods(newCurrentRecipient)
        updateCurrentRecipient(newCurrentRecipient);
    },[checkDeliveryMethods, currentRecipient, viewModelService]);

    const setRecipientContact = (id) => {
        if(id){
            const contact = potentialRecipients.find((recipient) => recipient.publicID === id || !recipient.publicID && String(recipient.tempID) === id);
            if(contact){
                handleChange(contact, 'contact');
            }
        }else{
            handleChange(undefined, 'contact');
        }
    }

    const shouldShowNewContactInputs = () => {
        return !!(_.get(currentRecipient, 'contact.value') && !_.get(currentRecipient, 'contact.publicID.value'));
    };

    const addRecipientButtonValid = () => {
        if (!currentRecipient?.contact.value) {
            return false;
        }

        if (!(_.get(currentRecipient, 'contact.emailAddress1.value') && _.get(currentRecipient, 'contact.emailAddress1.aspects.valid') ) && (_.get(currentRecipient, 'distributionMethod.value.code') === 'email' || _.get(currentRecipient, 'distributionMethod.value.code') === 'esignature_ml')) {
            return false;
        }

        if (!(_.get(currentRecipient, 'contact.faxNumber.value') && _.get(currentRecipient, 'contact.faxNumber.aspects.valid') ) && _.get(currentRecipient, 'distributionMethod.value.code') === 'fax_ml') {
            return false;
        }

        if (!_.get(currentRecipient, 'distributionMethod.value')) {
            return false;
        }

        return true;
    };

    const checkNumberOfRecipients = (_chosenRecipients)  => {
        alertService.clearBlockAlerts('emailCountMessage');
        alertService.clearBlockAlerts('faxCountMessage');


        const numberOfEmailRecipients = _chosenRecipients.filter((recipient) => {
            return recipient.distributionMethod === 'email';
        }).length;

        const numberOfFaxRecipients = _chosenRecipients.filter((recipient) => {
            return recipient.distributionMethod === 'fax_ml';
        }).length;

        setEmailRecipientsCount(numberOfEmailRecipients)
        setFaxRecipientsCount(numberOfFaxRecipients)
        
        if (numberOfEmailRecipients >= 2) {
            alertService.addWarningAlert(messages.cannotAddMoreEmailRecipients, 'emailCountMessage');
        }

        if (numberOfFaxRecipients >= 1) {
            alertService.addWarningAlert(messages.cannotAddMoreFaxRecipients, 'faxCountMessage');
        }


    }

    const addRecipient = () => {
        alertService.clearBlockAlerts('documentRecipientMsg');

        if (tagServiceFormName) {
            MLTagManagerService.sendLinkClickEvent(`${tagServiceFormName}_Add`);
        }

        if (currentRecipient && currentRecipient.aspects.valid && currentRecipient.aspects.subtreeValid) {

            if (isMultipleSigner) {
                if (chosenRecipients.length) {
                    if (currentRecipient.contact.emailAddress1.value === chosenRecipients[0].contact.emailAddress1) {
                        alertService.addErrorAlert(messages.duplicatedEmailAlert, 'documentRecipientMsg')

                        return;
                    }
                }

                const _chosenRecipients = [...chosenRecipients];

                _chosenRecipients.push(currentRecipient.value);

                const _currentRecipient = _initCurrentRecipient();

                updateIsInputDisabled(true);

                if (_chosenRecipients.length < Object.keys(multipleSigners).length) {
                    if (_chosenRecipients[0].distributionMethod === E_SIG) {
                        alertService.addWarningAlert(messages.eSignAlert, 'documentRecipientMsg');
                        _initMultipleSigner(multipleSigners.secondSigner, true, _currentRecipient);
                    }
                    alertService.addWarningAlert(messages.nonESignAlert, 'documentRecipientMsg');
                }

                updateChosenRecipients(_chosenRecipients);
                updateCurrentRecipient(_currentRecipient);
            } else {
                const _chosenRecipients = [...chosenRecipients];
                
                _chosenRecipients.push(currentRecipient.value);

                const _currentRecipient = _initCurrentRecipient();
                
                updateChosenRecipients(_chosenRecipients);
                updateCurrentRecipient(_currentRecipient);
                _initOtherRecipient();                
                checkNumberOfRecipients(_chosenRecipients)
            }
        }
    };

    const removeRecipient = (recipient) => {
        if (tagServiceFormName) {
            MLTagManagerService.sendLinkClickEvent(`${tagServiceFormName}_Remove`);
        }

        let _chosenRecipients = [...chosenRecipients];
        const idx = _chosenRecipients.indexOf(recipient);

        if (idx > -1) {
            _chosenRecipients.splice(idx, 1);
        }

        if (isMultipleSigner) {
            alertService.clearAlerts();
            _chosenRecipients = [];

            const _currentRecipient = viewModelService.clone(currentRecipient);

            _initMultipleSigner(multipleSigners.primarySigner, false, _currentRecipient, _chosenRecipients);
            updateIsInputDisabled(false);
            updateCurrentRecipient(_currentRecipient);
        }

        updateChosenRecipients(_chosenRecipients);
        checkNumberOfRecipients(_chosenRecipients)
    };

    const setDistributionMethodDropdownValues = () => {
        let filteredValues = _.get(currentRecipient, 'distributionMethod.aspects.availableValues', [])

        if (!canEmailDocument || emailRecipientsCount >= 2) {
            filteredValues = filteredValues.filter((value) => {
                return value.code !== 'email';
            })
        }

        if (faxRecipientsCount >= 1) {
            filteredValues = filteredValues.filter((value) => {
                return value.code !== 'fax_ml';
            })
        }

        return filteredValues.map((value) => {
            return {
                code: value.code,
                name: translator({id: value.name, defaultMessage: value.name})
            }
        })
    }

    const overrideProps = {
        addRecipients: {
            availableValues: addRecipientsAvailableValues(),
            disabled: isinputDisabled,
            onValueChange: setRecipientContact,
            value: _.get(currentRecipient, 'contact.value.publicID') || _.get(currentRecipient, 'contact.value.tempID')
        },
        newContactInformation: {
            visible: shouldShowNewContactInputs()
        },
        newContactDetails: {
            personVM: _.get(currentRecipient, 'contact'),
            onModelChange: (value, path) => handleChange(value, `contact.${path}`)
        },
        newContactAddress: {
            addressVM: _.get(currentRecipient, 'contact.primaryAddress'),
            onModelChange: (value, path) => handleChange(value, `contact.primaryAddress.${path}`),
            layout: "ml-policy-change",
            cityStateLookUp: true
        },
        distributionMethod: {
            disabled: isinputDisabled,
            availableValues: setDistributionMethodDropdownValues(),
            value: _.get(currentRecipient, 'distributionMethod.value'),
            onValueChange: (value) => handleChange(value, 'distributionMethod')
        },
        distributionDataRow: {
            visible: !!_.get(currentRecipient, 'contact.value')
        },
        distributionEmail: {
            visible: _.get(currentRecipient, 'distributionMethod.value.code') === 'email' || _.get(currentRecipient, 'distributionMethod.value.code') === 'esignature_ml'
        },
        distributionFax: {
            visible: _.get(currentRecipient, 'distributionMethod.value.code') === 'fax_ml'
        },
        distributionUsps: {
            visible: _.get(currentRecipient, 'distributionMethod.value.code') === 'usps_ml'
        },
        addBtn: {
            disabled: !addRecipientButtonValid()
        },
        chosenRecipientsTable: {
            chosenRecipients,
            getContactDisplayName,
            removeRecipient,
            viewModelService
        }
    };

    const resolvers = {
        resolveCallbackMap: {
            addRecipient
        },
        resolveComponentMap: {
            MLNameDetailsComponent,
            MLAddressDetailsComponent,
            MLChosenRecipientsTable
        }
    };

    return (
        <ViewModelForm
            uiProps={metadata.componentContent}
            overrideProps={overrideProps}
            model={currentRecipient}
            onValueChange={handleChange}
            callbackMap={resolvers.resolveCallbackMap}
            componentMap={resolvers.resolveComponentMap}
            classNameMap={styles}
        />
    );
}

export default MLDocumentRecipients;